import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import PhygitalIcon from "@/components/PhygitalIcon";
import EarlyAccessModal from "@/components/EarlyAccessModal";
import SharedFooter from "@/components/SharedFooter";
import {
  Heart,
  ArrowRight,
  CheckCircle2,
  Sparkles,
  Eye,
  Send,
  Share,
  Scissors,
  Palette,
  Calendar,
  Image,
  BookmarkPlus,
  Folder,
  Coffee,
  ShoppingBag,
  Plane,
  Leaf,
  Dumbbell,
  Star,
  GraduationCap,
  PartyPopper,
  ChefHat,
  User,
  Plus,
  Clock,
  MapPin,
  Check,
  Zap,
  Target,
  TrendingUp
} from "lucide-react";

const CATEGORIES = [
  { id: "hair", label: "Hair", icon: Scissors, color: "#F59E0B" },
  { id: "makeup", label: "Makeup", icon: Sparkles, color: "#EC4899" },
  { id: "nails", label: "Nails", icon: Palette, color: "#8B5CF6" },
  { id: "lashes", label: "Lashes", icon: Eye, color: "#7C3AED" },
  { id: "events", label: "Events", icon: PartyPopper, color: "#6366F1" },
  { id: "instructor", label: "Instructor", icon: GraduationCap, color: "#3B82F6" },
  { id: "culinary", label: "Culinary", icon: ChefHat, color: "#F97316" },
  { id: "wellness", label: "Wellness", icon: Dumbbell, color: "#10B981" },
  { id: "travel", label: "Travel", icon: Plane, color: "#0EA5E9" },
  { id: "style", label: "Retail", icon: ShoppingBag, color: "#6366F1" },
  { id: "dispensary", label: "Dispensary", icon: Leaf, color: "#22C55E" },
];

export default function PlanningExperienceDemo() {
  const [showEarlyAccess, setShowEarlyAccess] = useState(false);
  const [activeFeature, setActiveFeature] = useState("defaults");

  useEffect(() => {
    document.title = "phygital - Your Personal Profile";
  }, []);

  const features = {
    defaults: {
      name: "Save Your Preferences",
      icon: Heart,
      iconColor: "text-pink-500",
      description: "Set your personal defaults once, share them everywhere",
      details: [
        "Save preferences for every category you use",
        "Your defaults prefill automatically when you RSVP",
        "Edit anytime, share with any provider",
        "One place for dietary, accessibility, beauty, wellness and more"
      ],
      preview: [
        { label: "Dietary", value: "Vegan, Nut allergy", block: "dietary" },
        { label: "Hair", value: "Curly, 3C texture, Color treated", block: "hair" },
        { label: "Accessibility", value: "Wheelchair accessible, ASL preferred", block: "accessibility" },
      ]
    },
    inspo: {
      name: "Collect Inspiration",
      icon: Image,
      iconColor: "text-purple-500",
      description: "Save photos and references organized by category",
      details: [
        "One folder per category you have access to",
        "Save photos, links, screenshots, notes",
        "Attach inspo when planning experiences or sharing with providers",
        "Your provider sees exactly what you want"
      ],
      preview: [
        { label: "Hair", count: 12, color: "#F59E0B" },
        { label: "Nails", count: 8, color: "#8B5CF6" },
        { label: "Makeup", count: 5, color: "#EC4899" },
      ]
    },
    experience: {
      name: "Plan Experiences",
      icon: Calendar,
      iconColor: "text-indigo-500",
      description: "Plan around personal events or things you have coming up",
      details: [
        "Select a category, your defaults and inspo are already there",
        "Optionally link to a personal event or upcoming RSVP",
        "Add notes, pick inspo from your collection",
        "Share the full bundle with your provider in one link"
      ],
      preview: [
        { title: "Birthday Prep", category: "hair", status: "Shared Preferences" },
        { title: "Wedding Guest Look", category: "makeup", status: "Confirm" },
        { title: "Self Care Sunday", category: "wellness", status: "Completed" },
      ]
    },
    share: {
      name: "Share with Providers",
      icon: Send,
      iconColor: "text-blue-500",
      description: "One link with your preferences, inspo, and notes",
      details: [
        "Provider sees your defaults + inspo + notes in one view",
        "No back and forth, no screenshots, no miscommunication",
        "Works for bookings, RSVPs, or just sharing what you want",
        "QR code and link sharing built in"
      ],
      preview: []
    },
  };

  const currentFeature = features[activeFeature];

  return (
    <div className="min-h-screen bg-white" data-testid="planning-demo-page">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2">
            <PhygitalIcon className="w-8 h-8" variant="light" />
            <span className="font-semibold text-xl">phygital</span>
          </Link>
          <div className="flex items-center gap-4">
            <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
              <User className="w-3 h-3 mr-1" />
              Personal Profile
            </Badge>
            <Button
              onClick={() => setShowEarlyAccess(true)}
              className="bg-black hover:bg-gray-800 text-white"
              data-testid="join-pilot-btn"
            >
              Get Early Access
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 px-4">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50" />
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-64 h-64 bg-pink-200 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-80 h-80 bg-indigo-200 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-4xl mx-auto text-center">
          <Badge className="mb-6 bg-white/80 text-gray-700 border border-gray-200">
            <Sparkles className="w-3 h-3 mr-1" />
            Your preferences, perfectly organized
          </Badge>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 tracking-tight">
            <span className="bg-gradient-to-r from-gray-900 via-gray-700 to-gray-900 bg-clip-text text-transparent">
              Share what you want,
            </span>
            <br />
            <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 bg-clip-text text-transparent">
              before you even arrive.
            </span>
          </h1>
          
          <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Save your preferences and inspiration in one place. 
            Plan experiences around what matters to you. 
            Share everything with your provider in a single link.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={() => setShowEarlyAccess(true)}
              size="lg"
              className="bg-black hover:bg-gray-800 text-white h-12 px-8 rounded-full"
              data-testid="hero-join-btn"
            >
              Get Early Access
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="border-gray-300 text-gray-700 hover:bg-gray-50 h-12 px-8 rounded-full"
            >
              See How It Works
            </Button>
          </div>
        </div>
      </section>

      {/* Categories Bar */}
      <section className="py-8 px-4 border-y bg-gray-50/50">
        <div className="max-w-6xl mx-auto">
          <p className="text-center text-xs font-medium text-gray-400 uppercase tracking-wider mb-4">
            Preferences across 11 categories
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {CATEGORIES.map(cat => {
              const Icon = cat.icon;
              return (
                <div key={cat.id} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-gray-100 text-sm">
                  <Icon className="w-3.5 h-3.5" style={{ color: cat.color }} />
                  <span className="text-gray-600">{cat.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4">
            Everything you need in one profile
          </h2>
          <p className="text-gray-600 text-center mb-10 max-w-xl mx-auto">
            Save preferences, collect inspiration, plan experiences, and share it all seamlessly.
          </p>
          
          {/* Feature Selector */}
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {Object.entries(features).map(([key, feature]) => (
              <Button
                key={key}
                variant={activeFeature === key ? "default" : "outline"}
                className={`rounded-full ${activeFeature === key ? 'bg-black hover:bg-gray-800' : 'hover:bg-gray-50'}`}
                onClick={() => setActiveFeature(key)}
                data-testid={`feature-${key}`}
              >
                <feature.icon className={`w-4 h-4 mr-2 ${activeFeature === key ? 'text-white' : feature.iconColor}`} />
                {feature.name}
              </Button>
            ))}
          </div>
          
          {/* Selected Feature Card */}
          <Card className="max-w-3xl mx-auto border-gray-100">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center">
                  <currentFeature.icon className={`w-6 h-6 ${currentFeature.iconColor}`} />
                </div>
                <div>
                  <CardTitle>{currentFeature.name}</CardTitle>
                  <p className="text-sm text-gray-600">{currentFeature.description}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Details */}
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2 text-sm text-gray-700">
                    <Zap className="w-4 h-4 text-gray-400" />
                    What you can do
                  </h4>
                  <ul className="space-y-2">
                    {currentFeature.details.map((detail, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                        <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Preview */}
                <div>
                  {activeFeature === "defaults" && (
                    <div>
                      <h4 className="font-medium mb-3 flex items-center gap-2 text-sm text-gray-700">
                        <Heart className="w-4 h-4 text-pink-500" />
                        Your saved defaults
                      </h4>
                      <div className="space-y-2">
                        {currentFeature.preview.map((p, idx) => (
                          <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                            <p className="text-xs font-medium text-gray-500 mb-0.5">{p.label}</p>
                            <p className="text-sm text-gray-700">{p.value}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {activeFeature === "inspo" && (
                    <div>
                      <h4 className="font-medium mb-3 flex items-center gap-2 text-sm text-gray-700">
                        <Folder className="w-4 h-4 text-purple-500" />
                        Your inspo folders
                      </h4>
                      <div className="space-y-2">
                        {currentFeature.preview.map((p, idx) => (
                          <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-100 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: p.color + "15" }}>
                                <Image className="w-4 h-4" style={{ color: p.color }} />
                              </div>
                              <span className="text-sm font-medium">{p.label}</span>
                            </div>
                            <Badge variant="outline" className="text-xs">{p.count} saved</Badge>
                          </div>
                        ))}
                        <div className="p-3 border-2 border-dashed border-gray-200 rounded-lg text-center">
                          <p className="text-xs text-gray-400">More folders unlock with category access</p>
                        </div>
                      </div>
                    </div>
                  )}
                  {activeFeature === "experience" && (
                    <div>
                      <h4 className="font-medium mb-3 flex items-center gap-2 text-sm text-gray-700">
                        <Calendar className="w-4 h-4 text-indigo-500" />
                        Your experiences
                      </h4>
                      <div className="space-y-2">
                        {currentFeature.preview.map((p, idx) => {
                          const cat = CATEGORIES.find(c => c.id === p.category);
                          const Icon = cat?.icon || Star;
                          return (
                            <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-100 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Icon className="w-4 h-4" style={{ color: cat?.color }} />
                                <span className="text-sm font-medium">{p.title}</span>
                              </div>
                              <Badge variant="outline" className="text-[10px]">{p.status}</Badge>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {activeFeature === "share" && (
                    <div className="text-center py-6">
                      <div className="w-20 h-20 rounded-2xl bg-black flex items-center justify-center mx-auto mb-4">
                        <Send className="w-8 h-8 text-white" />
                      </div>
                      <p className="text-sm font-medium mb-1">One link, everything included</p>
                      <p className="text-xs text-gray-500">Preferences + Inspo + Notes</p>
                      <div className="flex gap-2 justify-center mt-4">
                        <Button size="sm" className="bg-black text-white hover:bg-gray-800 rounded-full gap-1">
                          <Share className="w-3 h-3" /> Share Card
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full gap-1">
                          <Image className="w-3 h-3" /> QR Code
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4">
            How it works
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-xl mx-auto">
            From saving preferences to sharing with providers, all in one seamless flow
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {[
              { step: "1", title: "Set your defaults", desc: "Save preferences per category once. They follow you everywhere.", icon: Heart, color: "bg-pink-100 text-pink-600" },
              { step: "2", title: "Collect inspo", desc: "Save photos and references into your category folders.", icon: Image, color: "bg-purple-100 text-purple-600" },
              { step: "3", title: "Plan an experience", desc: "Pick a category, your defaults and inspo are already there.", icon: Calendar, color: "bg-indigo-100 text-indigo-600" },
              { step: "4", title: "Share with provider", desc: "One link with everything. No back and forth needed.", icon: Send, color: "bg-blue-100 text-blue-600" },
            ].map(s => (
              <div key={s.step} className="text-center">
                <div className={`w-16 h-16 rounded-full ${s.color.split(" ")[0]} flex items-center justify-center mx-auto mb-4`}>
                  <span className={`text-2xl font-bold ${s.color.split(" ")[1]}`}>{s.step}</span>
                </div>
                <h3 className="font-semibold text-lg mb-2">{s.title}</h3>
                <p className="text-gray-600 text-sm">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-10">
            Why people love their phygital profile
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-gray-100 hover:shadow-md transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center mx-auto mb-4">
                  <Target className="w-6 h-6 text-pink-600" />
                </div>
                <h3 className="font-semibold mb-2">No more repeating yourself</h3>
                <p className="text-sm text-gray-600">
                  Set your preferences once. They prefill every time you RSVP or book a service.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-gray-100 hover:shadow-md transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="font-semibold mb-2">Better results, every time</h3>
                <p className="text-sm text-gray-600">
                  Providers know exactly what you want before you walk in. No miscommunication.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-gray-100 hover:shadow-md transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-4">
                  <Star className="w-6 h-6 text-indigo-600" />
                </div>
                <h3 className="font-semibold mb-2">Your look, your way</h3>
                <p className="text-sm text-gray-600">
                  Save inspo, link it to your preferences, and share the full picture with one tap.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* The Experience Flow */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4">
            Plan any experience, seamlessly
          </h2>
          <p className="text-gray-600 text-center mb-10 max-w-xl mx-auto">
            Whether it is a birthday, a wedding, a self care day, or just getting your nails done
          </p>

          <div className="space-y-4">
            {[
              { 
                category: "hair", title: "Birthday Prep",
                context: "Linked to: Birthday Weekend (Apr 12)",
                defaults: "Hair: Curly, 3C, Color treated",
                inspo: "3 photos attached",
                notes: "Want a blowout + trim, similar to last time but shorter",
                status: "Shared Preferences",
                provider: "Studio 26"
              },
              { 
                category: "makeup", title: "Wedding Guest Look",
                context: "Linked to: Sarah's Wedding RSVP",
                defaults: "Face: Warm undertone, Medium coverage, Dewy finish",
                inspo: "5 photos attached",
                notes: "Soft glam, nothing too heavy, outdoor ceremony",
                status: "Confirm",
                provider: "Glow by Maya"
              },
              { 
                category: "nails", title: "Gel Set Refresh",
                context: "Personal, no linked event",
                defaults: "Nails: Almond shape, Medium length",
                inspo: "2 photos attached",
                notes: "French tips, keep it clean",
                status: "Completed",
                provider: "The Nail Bar"
              },
            ].map((exp, idx) => {
              const cat = CATEGORIES.find(c => c.id === exp.category);
              const Icon = cat?.icon || Star;
              return (
                <Card key={idx} className="border-gray-100 overflow-hidden">
                  <CardContent className="p-0">
                    <div className="flex">
                      <div className="w-1.5 shrink-0" style={{ backgroundColor: cat?.color }} />
                      <div className="p-4 flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: cat?.color + "15" }}>
                              <Icon className="w-4 h-4" style={{ color: cat?.color }} />
                            </div>
                            <div>
                              <p className="font-semibold text-sm">{exp.title}</p>
                              <p className="text-[10px] text-gray-400">{exp.context}</p>
                            </div>
                          </div>
                          <Badge variant="outline" className="text-[10px] shrink-0">{exp.status}</Badge>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                          <div className="bg-gray-50 rounded p-2">
                            <p className="text-gray-400 mb-0.5">Defaults</p>
                            <p className="text-gray-600">{exp.defaults}</p>
                          </div>
                          <div className="bg-gray-50 rounded p-2">
                            <p className="text-gray-400 mb-0.5">Inspo</p>
                            <p className="text-gray-600">{exp.inspo}</p>
                          </div>
                          <div className="bg-gray-50 rounded p-2">
                            <p className="text-gray-400 mb-0.5">Notes</p>
                            <p className="text-gray-600">{exp.notes}</p>
                          </div>
                        </div>
                        {exp.provider && (
                          <p className="text-[10px] text-gray-400 mt-2">Shared with: {exp.provider}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-gray-900 to-black text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Your preferences, your way.
          </h2>
          <p className="text-gray-300 text-lg mb-8">
            Get early access and start building your personal profile today.
          </p>
          <Button
            onClick={() => setShowEarlyAccess(true)}
            size="lg"
            className="bg-white text-black hover:bg-gray-100 h-12 px-8 rounded-full font-semibold"
            data-testid="cta-join-btn"
          >
            Get Early Access
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <SharedFooter />

      {/* Early Access Modal */}
      <EarlyAccessModal
        open={showEarlyAccess}
        onOpenChange={setShowEarlyAccess}
        category="personal"
      />
    </div>
  );
}
